import { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Button, Box, CircularProgress } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { FiExternalLink } from 'react-icons/fi';
import { FaMapMarkerAlt } from 'react-icons/fa';

import { api, IWaypointMarker, WaypointMarkerType } from "../../../logic/api";
import { SessionContext } from "../../../utility/useSession";
import usePaths from "../../../utility/paths";
import { IColumnOptions } from './table-options'
import { useGoogleMaps } from '../../../context/google-maps/google-maps';
import AlertDialog from '../../../components/notifications/alertDialog/AlertDialog';
import { useReleaseManagement, ReleaseFlag } from '../../../utility/feature-management';


interface IJourneyMapButtonProps {
  journeyUUID: string;
}

const JourneyMapButton: FC<IJourneyMapButtonProps> = ({ journeyUUID }) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const { openMap, setMarkers, setEncodedPolyline } = useGoogleMaps()
  const [isLoading, setIsLoading] = useState(false)
  const [clicked, setClicked] = useState(false)
  const [showNoData, setShowNoData] = useState(false)
  const isNoMapDataPromptEnabled = useReleaseManagement(ReleaseFlag.releaseTelematicsMapNoDataPrompt)
  const getTelematicsWaypoints = api.hooks.chart.getTelematicsWaypoints()

  const handleClick = () => {
    setClicked(true);

    if (getTelematicsWaypoints.data?.waypoints) {
      setMarkers(getTelematicsWaypoints.data.waypoints.markers)
      setEncodedPolyline(getTelematicsWaypoints.data.waypoints.encodedPolyline)
      openMap()

      return
    }

    if (!isLoading) {
      setIsLoading(true)

      getTelematicsWaypoints.send(session.current.context, { journeyUUID }, (res) => {
        setIsLoading(false)

        if (res?.waypoints) {
          setMarkers(res.waypoints.markers)
          setEncodedPolyline(res.waypoints.encodedPolyline)
          openMap()
        } else if (isNoMapDataPromptEnabled) {
          setShowNoData(true)
        }
      })
    }
  }

  return (
    <>
      <Button
        data-testid="journey-map-button"
        variant="outlined"
        color={clicked ? "secondary" : "primary"}
        disabled={isLoading}
        onClick={handleClick}
        endIcon={isLoading && isNoMapDataPromptEnabled ? <CircularProgress size={14} /> : <FaMapMarkerAlt size={14} />}
      >
        {t("charts.buttons.openMap")}
      </Button>
      <AlertDialog
        title={""} // No title was required for now
        message={t("charts.buttons.noMapData")}
        show={showNoData}
        onClose={() => setShowNoData(false)}
      />
    </>
  );
}


interface IIncidentMapButtonProps {
  marker: IWaypointMarker
}

const IncidentMapButton: FC<IIncidentMapButtonProps> = ({ marker }) => {
  const { t } = useTranslation();
  const { openMap, setMarkers, setZoom } = useGoogleMaps()
  const [clicked, setClicked] = useState(false)

  const handleClick = () => {
    setClicked(true)
    setMarkers([marker])
    setZoom(16)
    openMap()
  };

  return (
    <Button
      variant="outlined"
      color={clicked ? "secondary" : "primary"}
      onClick={handleClick}
      endIcon={<FaMapMarkerAlt size={14} />}
    >
      {t("charts.buttons.openMap")}
    </Button>
  );
}


interface IBookingRefButtonProps {
  bookingRef: string;
}

const BookingRefButton: FC<IBookingRefButtonProps> = ({ bookingRef }) => {
  const session = useContext(SessionContext);
  const paths = usePaths();
  const [isLoading, setIsLoading] = useState(false);
  const [clicked, setClicked] = useState(false);

  const getByRef = api.hooks.booking.getByRef()

  const handleClick = () => {
    setClicked(true);
    // This is rather hacky - we need to fetch the booking ID via ref before navigating
    // Will ideally be fixed in the future where the ID is made available to us without this hurdle
    if (!isLoading) {
      setIsLoading(true);

      try {
        getByRef.send(session.current.context, { ref: bookingRef }, (booking) => {
          if (!booking) {
            console.error(`Unable to load booking: No booking was found for ref ${String(bookingRef)}`);
            return;
          }

          const bookingUrl = paths.booking(booking.id);
          window.open(bookingUrl, '_blank');
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Button
      variant="text"
      color={clicked ? "secondary" : "primary"}
      disabled={isLoading}
      onClick={handleClick}
      endIcon={<FiExternalLink size={14} />}
      title="Open booking in another tab"
    >
      {bookingRef}
    </Button>
  )
}


interface ITableCellProps extends GridRenderCellParams {
  options?: IColumnOptions;
}

export const TableCell: FC<ITableCellProps> = ({ value, options, row }) => {
  if (options?.isJourneyMapButton && typeof value === "string" && value.length) {
    return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: "100%" }}>
      <JourneyMapButton journeyUUID={value} />
    </Box>
  }

  if (options?.isBookingRefButton && typeof value === "string" && value.length) {
    return <BookingRefButton bookingRef={value} />
  }

  if (options?.isIncidentMapButton) {
    if (!row.latitude || !row.longitude || !row.time || !row.type) {
      console.error("Couldn't load map due to an invalid `row`.")
      return <Typography variant="caption" sx={{ fontSize: 14 }}>Failed to load map</Typography>
    }

    const marker: IWaypointMarker = {
      lng: Number(row.longitude),
      lat: Number(row.latitude),
      type: Number(WaypointMarkerType[row.type]),
      timestamp: row.time,
      speed: row.speed,
      speedLimit: row.speedLimit,
      strength: Number(row.strength)
    }

    return <IncidentMapButton marker={marker} />
  }

  return <Typography variant="caption" sx={{ fontSize: 14 }}>{value}</Typography>
};
